/* ============================================================
   GH-1133 S5 — free preview before the card: the pure layer.
   Design: dev-artifacts/docs/FREE-PREVIEW-DESIGN-2026-10-13.md (root
   staging) §2.3 board, §2.5 wall, §2.6 edge states, §3.9 SPA, §3.10 events.

   Server contracts this reads (all merged on the service staging branches):
   - gateway S2 (#43): GET /api/billing/entitlements → data.freePreview, only
      for an explicitly granted org with accessState 'new' or 'free':
       {active:true, readOnly:false, experiment, drafts:{ready,limit}, images:{limit}, trialDays:7}
       {active:true, readOnly:true, …}            rollback (preview/enabled=0)
       {active:false, hidden:true}                preview/hide_ui=1
       {active:false, pendingVerification:true}   password signup, unverified
     and a 402 {code:'BILLING_REQUIRED', details:{preview:{reason, draftsReady,
     draftsLimit, trialDays, copyKey}}} for every other gated write.
   - content S3 (#67): POST /api/content/preview/drafts → 202 queued/inProgress,
     200 complete/brand_incomplete/eligible:false, 404 = preview off, 409 =
     research running. Drafts are content_items source 'preview', status
     'ready', format 'text_post', platformVersions.__previewMeta.planItemIndex.

   Nothing here touches the network or the DOM except copyText (injected).
   ============================================================ */
import { planHasTrial } from './planTrial.js';
import { platformFromVersions } from './platforms.js';

export const FREE_PREVIEW_EXPERIMENT = 'free_preview_v1';
export const PREVIEW_RESUME_KEY = 'dathent.freePreview.resume';
export const FREE_PREVIEW_STATE_MODES = ['active', 'readOnly', 'verify', 'hidden'];
const RESUME_MAX_AGE_MS = 2 * 60 * 60 * 1000;
const DEFAULT_LIMIT = 3;

// §3.10 — the 21 client event names, listed one by one (the gateway admin
// whitelist lists the same names; TASK-143's preview_paywall_* share the
// prefix, so nothing may ever filter on a bare "preview_").
export const PREVIEW_EVENT_NAMES = [
  'preview_started',
  'preview_celebrate_viewed', 'preview_connect_clicked', 'preview_board_viewed',
  'preview_snapshot_opened', 'preview_draft_opened', 'preview_draft_copied', 'preview_idea_clicked',
  'preview_slow_shown', 'preview_failed_shown', 'preview_locked_route_viewed', 'preview_verify_shown', 'preview_brand_incomplete_shown',
  'preview_wall_shown', 'preview_wall_dismissed', 'preview_checkout_started', 'preview_checkout_cancelled',
  'preview_card_added', 'preview_resume_shown', 'preview_resume_clicked', 'preview_returned',
];

export const WALL_SOURCES = ['publish', 'schedule', 'generate', 'image', 'edit', 'research', 'feature', 'cta'];

const CONTEXT_KEY_BY_SOURCE = {
  publish: 'freePreview.wallPublish',
  schedule: 'freePreview.wallSchedule',
  generate: 'freePreview.wallGenerate',
  image: 'freePreview.wallImage',
  edit: 'freePreview.wallEdit',
  research: 'freePreview.wallResearch',
  feature: 'freePreview.wallFeature',
  cta: 'freePreview.wallDefault',
};

const RESEARCH_ACTIVE = ['queued', 'running', 'gathering', 'analyzing', 'synthesizing', 'verifying'];

function unwrapData(value) {
  if (value && typeof value === 'object' && value.data && typeof value.data === 'object' && !Array.isArray(value.data)) return value.data;
  return value || null;
}

function finiteInt(value, fallback) {
  const n = Number(value);
  return Number.isFinite(n) && n >= 0 ? Math.floor(n) : fallback;
}

/**
 * The free-preview block of an entitlements payload, normalised.
 * null = not a preview account (control, NULL arm, denied, other accessState).
 */
export function freePreviewState(entitlements) {
  const data = unwrapData(entitlements);
  if (!data || !['new', 'free'].includes(data.accessState)) return null;
  const block = data.freePreview;
  if (!block || typeof block !== 'object' || Array.isArray(block)) return null;
  if (block.hidden === true) return { mode: 'hidden' };
  if (block.pendingVerification === true) return { mode: 'verify' };
  if (block.active !== true) return null;
  const limit = finiteInt(block.drafts?.limit, DEFAULT_LIMIT) || DEFAULT_LIMIT;
  return {
    mode: block.readOnly === true ? 'readOnly' : 'active',
    drafts: { ready: finiteInt(block.drafts?.ready, 0), limit },
    imagesLimit: finiteInt(block.images?.limit, 1),
    trialDays: finiteInt(block.trialDays, 7) || 7,
  };
}

/** Does this error carry the gateway's free-preview 402 details? */
export function isPreviewBlocked(err) {
  return Boolean(err && Number(err.status) === 402 && err.details && typeof err.details.preview === 'object' && err.details.preview);
}

/** Gateway 402 reason (S2 table) → wall source. */
export function wallSourceForReason(reason) {
  switch (reason) {
    case 'generate': case 'edit': case 'publish': case 'image': case 'schedule': case 'research': case 'feature':
      return reason;
    case 'strategy':
      return 'feature';
    default:
      return 'cta';
  }
}

/**
 * UpgradeContext `feature` (quota/research/autopilot/preview/default) → wall
 * source. A wall source passed as the feature (an inline preview 402 mapped
 * with wallSourceForReason) is kept as is.
 */
export function wallSourceForFeature(feature) {
  if (WALL_SOURCES.includes(feature)) return feature;
  switch (feature) {
    case 'research': return 'research';
    case 'quota': return 'generate';
    case 'autopilot': return 'feature';
    default: return 'cta';
  }
}

/**
 * The wall's context line, as i18n keys, keyed on the selected plan the same
 * way PlanSelectScreen's showTrialSubtitle is: an AI plan (with a trial) gets
 * the source line + "trial hasn't started" + "stays saved"; the Scheduler gets
 * only its own no-trial line (never "free trial", "€0" or wallKeep).
 */
export function wallContextKeys(source, { aiPlan = true } = {}) {
  if (!aiPlan) return ['freePreview.wallPostingContext'];
  return [CONTEXT_KEY_BY_SOURCE[source] || CONTEXT_KEY_BY_SOURCE.cta, 'freePreview.trialNotStarted', 'freePreview.wallKeep'];
}

const WALL_PARAMS = ['checkout', 'session_id', 'wall'];

/**
 * Checkout returnTo supplied by the overlay. /app/plans → /app (a freshly
 * trialing user must not land on the pricing page); otherwise the underlying
 * /app path without checkout/wall params. Never leaves /app.
 */
export function wallReturnTo({ pathname = '/app', search = '', fromPlansRoute = false } = {}) {
  const path = String(pathname || '');
  if (fromPlansRoute || path === '/app/plans' || path.startsWith('/app/plans/')) return '/app';
  if (!(path === '/app' || path.startsWith('/app/')) || path.startsWith('//')) return '/app';
  const params = new URLSearchParams(search || '');
  WALL_PARAMS.forEach((key) => params.delete(key));
  const query = params.toString();
  return `${path}${query ? `?${query}` : ''}`;
}

function text(value) {
  return typeof value === 'string' ? value.trim() : '';
}

/**
 * Content list rows (GET /api/content) → the preview's drafts: the first post
 * (source first_post) first, then source 'preview' by creation time. Archived
 * rows are dropped (delete is hidden in the preview, so this is defensive).
 */
export function previewDraftsFromRows(rows) {
  const list = Array.isArray(rows) ? rows : (Array.isArray(rows?.content) ? rows.content : []);
  const drafts = [];
  for (const row of list) {
    const item = row?.contentItem || row || {};
    if (!item.id || (item.source !== 'first_post' && item.source !== 'preview') || item.archivedAt) continue;
    const version = row?.activeVersion || null;
    const body = text(version?.body) || text(version?.caption) || text(item.body);
    const imageUrl = text(version?.imageUrl) || text(row?.mediaPreview?.url) || (Array.isArray(row?.mediaUrls) ? text(row.mediaUrls[0]) : '') || null;
    const meta = version?.platformVersions?.__previewMeta;
    const index = meta && typeof meta === 'object' ? meta.planItemIndex : null;
    const platforms = Array.isArray(item.platforms) ? item.platforms : (Array.isArray(row?.jobPlatforms) ? row.jobPlatforms : []);
    drafts.push({
      id: item.id,
      source: item.source,
      title: text(version?.hook) || text(item.title) || text(item.topic) || body.split('\n')[0] || '',
      topic: text(item.topic) || null,
      body,
      imageUrl,
      isText: item.format === 'text_post' || !imageUrl,
      platform: platforms[0] || platformFromVersions(version?.platformVersions),
      planItemIndex: Number.isInteger(index) ? index : null,
      createdAt: item.createdAt || null,
    });
  }
  return drafts.sort((a, b) => {
    if (a.source !== b.source) return a.source === 'first_post' ? -1 : 1;
    return String(a.createdAt || '').localeCompare(String(b.createdAt || ''));
  });
}

function topicKey(value) {
  return text(value).toLowerCase().replace(/\s+/g, ' ');
}

/**
 * The 2 next idea titles for the short plan: the activation
 * strategy's weeklyPlan (same loose item shape the content-worker reads:
 * topic|title|idea|hook), minus the first-post topic and the plan items the
 * preview drafts were written from.
 */
export function pickIdeaTitles(strategyResponse, drafts = [], count = 2) {
  const data = unwrapData(strategyResponse);
  // GET /strategy returns a saved row whose `strategy` contains the plan.
  // Also accept the decoded plan/legacy response used by older callers.
  const strategy = data?.strategy?.strategy || data?.strategy || data;
  const plan = strategy?.weeklyPlan;
  if (!Array.isArray(plan)) return [];
  const skipTopics = new Set();
  const recommended = topicKey(strategy?.firstPostRecommendation?.topic);
  if (recommended) skipTopics.add(recommended);
  const usedIndices = new Set();
  let previewWithoutIndex = 0;
  for (const draft of drafts || []) {
    if (draft?.source === 'first_post') {
      [draft.topic, draft.title].forEach((value) => { const key = topicKey(value); if (key) skipTopics.add(key); });
    } else if (draft?.source === 'preview') {
      if (Number.isInteger(draft.planItemIndex)) usedIndices.add(draft.planItemIndex);
      else previewWithoutIndex += 1;
      const key = topicKey(draft.topic);
      if (key) skipTopics.add(key);
    }
  }
  const slots = [];
  plan.forEach((item, index) => {
    if (!item || typeof item !== 'object') return;
    const title = text(item.topic) || text(item.title) || text(item.idea) || text(item.hook);
    if (title.length < 3) return;
    if (usedIndices.has(index) || skipTopics.has(topicKey(title))) return;
    slots.push({ index, title: title.slice(0, 120), day: text(item.day) || null });
  });
  // Drafts without a recorded plan index were written from the first free
  // slots (content-worker keeps plan order), so skip that many.
  return slots.slice(previewWithoutIndex, previewWithoutIndex + count);
}

/** UI affordance only; the gateway rechecks ownership, grant and history. */
export function canEditPreviewCaption(preview, raw) {
  const item = raw?.contentItem;
  return Boolean(preview?.active && preview.mode === 'active' && !preview.readOnly
    && item?.id && item.brandProfileId && raw?.activeVersion?.id
    && ['first_post', 'preview'].includes(item.source)
    && ['text_post', 'image_post'].includes(item.format)
    && ['ready', 'draft'].includes(item.status) && !item.archivedAt
    && !(raw?.publishSummary?.targets?.length)
    && (item.generationStatus == null || item.generationStatus === 'ready')
    && !['queued', 'running', 'processing', 'generating', 'visualizing', 'pending'].includes(raw?.job?.visualStatus));
}

export function previewCaptionText(post) {
  const caption = typeof post?.caption === 'string' ? post.caption : '';
  const hashtags = Array.isArray(post?.hashtags) ? post.hashtags.filter(tag => typeof tag === 'string' && tag.trim()).map(tag => `#${tag.trim().replace(/^#+/, '')}`) : [];
  const tags = [...new Set(hashtags)].filter(tag => !caption.includes(tag));
  return tags.length ? `${caption}\n\n${tags.join(' ')}` : caption;
}

/**
 * The §2.3 board state. Inputs are server data only (no local markers).
 * snapshot: none | running | ready | failed (failed includes "never started").
 */
export function deriveBoardState({
  mode = 'active',
  brand = 'present',
  brandIncomplete = false,
  packReason = null,
  research = null,
  drafts = [],
  limit = DEFAULT_LIMIT,
  minutesSinceComplete = null,
  hasStrategy = false,
} = {}) {
  const readyCount = Array.isArray(drafts) ? drafts.length : 0;
  const draft1Ready = readyCount > 0;
  const status = research?.status || null;
  const minutes = Number.isFinite(minutesSinceComplete) ? minutesSinceComplete : null;
  let snapshot = 'none';
  if (status === 'ready') snapshot = 'ready';
  else if (status === 'failed' || status === 'cancelled') snapshot = 'failed';
  else if (status && RESEARCH_ACTIVE.includes(status)) snapshot = 'running';
  else if (!status && minutes !== null && minutes >= 5) snapshot = 'failed'; // research never started
  else if (status) snapshot = 'running';

  let key;
  if (mode === 'verify') key = 'BV';
  else if (brand === 'missing') key = 'B0';
  else if (readyCount >= limit) key = 'B5';
  else if ((brandIncomplete || packReason === 'brand_incomplete') && !draft1Ready) key = 'BB';
  else if (draft1Ready) key = 'B4';
  else if (minutes !== null && minutes >= 30) key = 'B8';
  else if (minutes !== null && minutes >= 12) key = 'B7';
  else if (snapshot === 'ready') key = 'B3';
  else if (snapshot === 'failed') key = 'B6';
  else if (snapshot === 'running') key = 'B2';
  else key = 'B1';

  const done = 1
    + (snapshot === 'ready' || snapshot === 'failed' ? 1 : 0)
    + (draft1Ready || hasStrategy ? 1 : 0)
    + (readyCount >= limit ? 1 : 0);
  return {
    key,
    snapshot,
    draft1Ready,
    readyCount: Math.min(readyCount, limit),
    limit,
    step: Math.min(done + (readyCount >= limit ? 0 : 1), 4),
    polling: !['BV', 'B0', 'B5', 'B8'].includes(key) && mode !== 'readOnly',
  };
}

/**
 * C3 (§2.0): the board's single write. Fires once per page session when the
 * pack is incomplete, no research run is in flight, the brand is not flagged
 * incomplete and ≥5 min have passed since onboarding completion. Never in the
 * read-only rollback board or before the email is verified.
 */
export function shouldRequestPreviewDrafts({
  mode, readyCount = 0, limit = DEFAULT_LIMIT, research = null, brandIncomplete = false,
  minutesSinceComplete = null, alreadyRequested = false,
} = {}) {
  if (mode !== 'active' || alreadyRequested) return false;
  if (readyCount >= limit) return false;
  if (research && RESEARCH_ACTIVE.includes(research.status)) return false;
  if (brandIncomplete) return false;
  return Number.isFinite(minutesSinceComplete) && minutesSinceComplete >= 5;
}

export function minutesSince(iso, now = Date.now()) {
  const ts = Date.parse(iso || '');
  return Number.isFinite(ts) ? Math.max(0, (now - ts) / 60000) : null;
}

/* ---------- resume intent (sessionStorage; written on Continue) ---------- */

// `plan` is the plan key picked on the wall ('posting' = the €3.99 Scheduler,
// billed today with no trial). The resume host needs it to word the
// after-payment toast and bar truthfully (see resumeTrialOn).
export function writeResumeIntent(storage, { source = 'cta', postId = null, plan = null } = {}, now = Date.now()) {
  const planKey = typeof plan === 'string' && plan ? plan : null;
  try { storage?.setItem(PREVIEW_RESUME_KEY, JSON.stringify({ source, postId: postId || null, plan: planKey, ts: now })); } catch { /* best effort */ }
}

export function readResumeIntent(storage, now = Date.now()) {
  try {
    const raw = storage?.getItem(PREVIEW_RESUME_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object' || typeof parsed.source !== 'string') return null;
    if (!Number.isFinite(parsed.ts) || now - parsed.ts > RESUME_MAX_AGE_MS) return null;
    return {
      source: parsed.source,
      postId: typeof parsed.postId === 'string' ? parsed.postId : null,
      plan: typeof parsed.plan === 'string' && parsed.plan ? parsed.plan : null,
      ts: parsed.ts,
    };
  } catch {
    return null;
  }
}

export function clearResumeIntent(storage) {
  try { storage?.removeItem(PREVIEW_RESUME_KEY); } catch { /* best effort */ }
}

/**
 * §2.1 principle 5 / §2.7: after the wall's checkout, may the toast and the
 * resume bar say "your trial is on"? Only when a trial actually runs: the
 * account is 'trialing' AND the plan picked on the wall has a trial. The wall
 * also sells the €3.99 Scheduler (billed today, no trial → accessState
 * 'active'), and an AI plan bought without a trial is 'active' too; both get
 * the neutral copy. A missing plan (an intent from the kill-switch path whose
 * activation carried no planKey) defers to the server's 'trialing'.
 * accessState wins; billingStatus is read only when accessState is absent.
 */
export function resumeTrialOn(entitlements, plan = null) {
  const data = entitlements?.data || entitlements || null;
  if (!data || typeof data !== 'object') return false;
  const trialing = typeof data.accessState === 'string' && data.accessState
    ? data.accessState === 'trialing'
    : data.billingStatus === 'trialing';
  return trialing && planHasTrial(plan || null);
}

/** C8: which resume-bar variant to offer after the card is added. */
export function resumeVariant({ hasPost = false, hasImage = false, hasChannel = false } = {}) {
  if (!hasPost) return 'create';
  if (!hasChannel) return 'connect';
  return hasImage ? 'publish' : 'text';
}

/* ---------- copy text (clipboard + iOS Safari fallback) ---------- */

export async function copyText(value, { nav = globalThis.navigator, doc = globalThis.document } = {}) {
  const str = String(value ?? '');
  try {
    if (nav?.clipboard?.writeText) {
      await nav.clipboard.writeText(str);
      return true;
    }
  } catch { /* fall through to the textarea path */ }
  try {
    if (!doc?.createElement || !doc.body) return false;
    const area = doc.createElement('textarea');
    area.value = str;
    area.setAttribute?.('readonly', '');
    if (area.style) { area.style.position = 'fixed'; area.style.opacity = '0'; area.style.top = '0'; }
    doc.body.appendChild(area);
    area.select?.();
    area.setSelectionRange?.(0, str.length);
    const ok = doc.execCommand ? doc.execCommand('copy') : false;
    doc.body.removeChild(area);
    return Boolean(ok);
  } catch {
    return false;
  }
}

/* ---------- reveal (§3.9 item 10) ---------- */

/**
 * The reveal holds the curtain (polls up to 60 s) for a missing image — except
 * for a text_post, which never gets one (preview drafts, preview/images=0).
 * Every other format keeps today's wait.
 */
export function shouldWaitForImage({ format, imageUrl } = {}) {
  if (imageUrl) return false;
  return format !== 'text_post';
}

/* ---------- mini-report ---------- */

export function siteFacts(brandProfile) {
  const bp = brandProfile && typeof brandProfile === 'object' ? brandProfile : {};
  const tone = Array.isArray(bp.toneOfVoice)
    ? bp.toneOfVoice.map(text).filter(Boolean).slice(0, 3).join(' · ')
    : text(bp.toneOfVoice);
  return {
    voice: tone || text(bp.customToneDescription) || null,
    audience: text(bp.targetAudience) || null,
    offer: text(bp.offer) || text(bp.productOrService) || text(bp.businessDescription) || null,
  };
}

export function marketSnapshot(report) {
  const segments = Array.isArray(report?.metrics?.audience?.segments) ? report.metrics.audience.segments : [];
  const sourceCount = Array.isArray(report?.sources) ? report.sources.length : 0;
  const top = segments
    .filter((s) => s && text(s.label) && Number.isFinite(Number(s.share)))
    .map((s) => ({ label: text(s.label), share: Number(s.share) }))
    .sort((a, b) => b.share - a.share)
    .slice(0, 3);
  if (top.length) return { segments: top, sourceCount };
  const archetypes = Array.isArray(report?.blocks?.archetypes) ? report.blocks.archetypes : [];
  return {
    segments: archetypes.filter((a) => text(a?.name)).slice(0, 3).map((a) => ({ label: text(a.name), share: null })),
    sourceCount,
  };
}
